<script>
  import { onMount } from 'svelte';
  import { currentUser, currentScreen, savedMatches } from '../store.js';
  import { pushUserProfileToCloud, checkUsernameAvailability, claimUsername, deleteAccountAndData, fetchMyPerformances, fetchUserProfile } from '../../services/cloud-sync.js';
  import { formatOvers } from '../../scoring-logic.js';
  import { auth } from '../../firebase-config.js';
  import { toast, confirmDialog, promptDialog, alertDialog } from '../../ui/dialogs.js';
  import { renderPlayerCardCanvas, saveCanvasImage } from '../../share-images.js';
  import { calculatePlayerMvpPoints, getMvpRankTier } from '../../mvp-engine.js';

  $: mvpPoints = calculatePlayerMvpPoints(stats);
  $: mvpTier = getMvpRankTier(mvpPoints);

  let isSharingCard = false;
  async function shareStatsCard() {
    isSharingCard = true;
    try {
      const canvas = await renderPlayerCardCanvas(
        { displayName: displayName || $currentUser?.displayName || 'Player', username, photo: photoBase64 },
        stats
      );
      const result = await saveCanvasImage(canvas, `${(username || displayName || 'player').replace(/\s+/g, '_')}_stats_card.png`);
      if (result.ok) toast('🖼️ Stats card ready — share it on WhatsApp/Instagram!', 'success');
      else toast('Card save failed: ' + (result.error?.message || ''), 'error');
    } catch (e) {
      toast('Card render failed: ' + (e.message || e), 'error');
    } finally {
      isSharingCard = false;
    }
  }

  let displayName = $currentUser?.displayName || '';
  let username = '';
  let phoneInput = $currentUser?.phoneNumber || '';
  let photoBase64 = '';
  let message = '';
  let errorMsg = '';
  let isSaving = false;
  let usernameLocked = false;
  let isCheckingUsername = false;

  // ── Player identity (role / style / type / age) ──
  let role = '';           // Batsman | Bowler | All-rounder | Wicketkeeper
  let battingStyle = '';   // Right-hand | Left-hand
  let bowlingType = '';    // Fast | Medium | Off-spin | Leg-spin | Left-arm-fast | Left-arm-spin
  let age = '';
  const ROLES = ['Batsman', 'Bowler', 'All-rounder', 'Wicketkeeper'];
  const BATTING_STYLES = ['Right-hand', 'Left-hand'];
  const BOWLING_TYPES = ['Fast', 'Medium', 'Off-spin', 'Leg-spin', 'Left-arm-fast', 'Left-arm-spin'];
  // Bowling type only matters for players who bowl.
  $: showBowlingType = role === 'Bowler' || role === 'All-rounder';

  // Career stats computed from savedMatches and cloudPerformances
  let stats = {
    matches: 0,
    wins: 0,
    losses: 0,
    winRate: 0,
    momCount: 0,

    runs: 0,
    balls: 0,
    outs: 0,
    notOuts: 0,
    fifties: 0,
    hundreds: 0,
    thirties: 0,
    sixes: 0,
    fours: 0,
    highestScore: 0,
    battingAvg: '0.0',
    strikeRate: '0.0',
    boundaryRunsPct: '0.0',

    wickets: 0,
    runsConceded: 0,
    ballsBowled: 0,
    maidens: 0,
    bestBowlingWickets: 0,
    bestBowlingRuns: 0,
    threeWickets: 0,
    fiveWickets: 0,
    bowlingAvg: '0.0',
    bowlingSR: '0.0',
    economy: '0.0',

    catches: 0,
    runOuts: 0,
    badges: []
  };

  $: if ($currentUser) {
    try {
      const reg = JSON.parse(localStorage.getItem('cs_player_registry') || '{}');
      const key = $currentUser.uid;
      if (reg[key]) {
        username = reg[key].username || '';
        photoBase64 = reg[key].photo || '';
        if (reg[key].phoneNumber && !$currentUser.phoneNumber) {
           phoneInput = reg[key].phoneNumber;
        }
        if (reg[key].username && reg[key].username.length > 0) {
          usernameLocked = true;
        }
        if (!role && reg[key].role) role = reg[key].role;
        if (!battingStyle && reg[key].battingStyle) battingStyle = reg[key].battingStyle;
        if (!bowlingType && reg[key].bowlingType) bowlingType = reg[key].bowlingType;
        if (!age && reg[key].age) age = reg[key].age;
      }
    } catch (_) {}
  }

  // Ecosystem: performances recorded by OTHER phones (matches you played but
  // didn't score yourself). Local savedMatches take priority — cloud docs for
  // matches already on this phone are skipped to avoid double counting.
  let cloudPerformances = [];
  onMount(async () => {
    if (!$currentUser?.uid) return;

    // CLOUD HYDRATION — the profile previously loaded ONLY from the local
    // player registry (localStorage), so a reinstall / new phone / cleared
    // data showed an empty profile even though everything was saved in the
    // cloud. Pull users/{uid} and fill anything the local cache is missing.
    try {
      const cloudProfile = /** @type {any} */ (await fetchUserProfile());
      if (cloudProfile) {
        if (!username && cloudProfile.username) {
          username = cloudProfile.username;
          usernameLocked = true;
        }
        if (cloudProfile.displayName && (!displayName || displayName === $currentUser.displayName)) {
          displayName = cloudProfile.displayName;
        }
        if (!photoBase64 && cloudProfile.photo) photoBase64 = cloudProfile.photo;
        if (!phoneInput && cloudProfile.phoneNumber) phoneInput = cloudProfile.phoneNumber;
        if (!role && cloudProfile.role) role = cloudProfile.role;
        if (!battingStyle && cloudProfile.battingStyle) battingStyle = cloudProfile.battingStyle;
        if (!bowlingType && cloudProfile.bowlingType) bowlingType = cloudProfile.bowlingType;
        if (!age && cloudProfile.age) age = cloudProfile.age;
        // Refresh the local registry cache so overlays/photos work offline too.
        try {
          const reg = JSON.parse(localStorage.getItem('cs_player_registry') || '{}');
          reg[$currentUser.uid] = { ...(reg[$currentUser.uid] || {}), ...cloudProfile, uid: $currentUser.uid };
          if (cloudProfile.displayName) reg[cloudProfile.displayName] = reg[$currentUser.uid];
          localStorage.setItem('cs_player_registry', JSON.stringify(reg));
        } catch (_) {}
      }
    } catch (error) {
      console.warn('[Profile] Cloud hydration failed:', error);
    }

    cloudPerformances = await fetchMyPerformances();
  });

  // Reactive career stats calculation based on displayName
  $: if (($savedMatches || cloudPerformances) && displayName) {
    calculateStats(displayName);
  }

  function calculateStats(name) {
    let matchesCount = 0, wins = 0, losses = 0, momCount = 0;
    let r = 0, b = 0, outs = 0, notOuts = 0, sx = 0, fr = 0, hs = 0;
    let fifties = 0, hundreds = 0, thirties = 0;
    let w = 0, rc = 0, bb = 0, md = 0, threeW = 0, fiveW = 0;
    let bestW = 0, bestR = 999;
    let catches = 0, runOuts = 0;

    $savedMatches.forEach(m => {
      if (!m.innings) return;
      const isPlayerInA = m.teamA?.players?.includes(name);
      const isPlayerInB = m.teamB?.players?.includes(name);
      if (!isPlayerInA && !isPlayerInB) return;

      matchesCount++;

      // Win / Loss tracking
      const playerTeam = isPlayerInA ? 'A' : 'B';
      if (m.result?.winner === playerTeam) wins++;
      else if (m.result?.winner && m.result?.winner !== 'TIE' && m.result?.winner !== 'COMPLETED') losses++;

      // MOM Award tracking
      if (m.result?.mom === name) momCount++;

      m.innings.forEach(inn => {
        const batObj = inn.batsmen.find(bt => bt.name === name);
        if (batObj) {
          const runsScored = batObj.runs || 0;
          r += runsScored;
          b += batObj.balls || 0;
          sx += batObj.sixes || 0;
          fr += batObj.fours || 0;
          if (runsScored > hs) hs = runsScored;

          if (batObj.out) outs++;
          else if ((batObj.balls || 0) > 0 || runsScored > 0) notOuts++;

          if (runsScored >= 100) hundreds++;
          else if (runsScored >= 50) fifties++;
          else if (runsScored >= 30) thirties++;
        }

        const bowlObj = inn.bowlers.find(bw => bw.name === name);
        if (bowlObj && (bowlObj.balls || 0) > 0) {
          const wkts = bowlObj.wickets || 0;
          const runsGv = bowlObj.runs || 0;
          w += wkts;
          rc += runsGv;
          bb += bowlObj.balls || 0;
          md += bowlObj.maidens || 0;

          if (wkts >= 5) fiveW++;
          else if (wkts >= 3) threeW++;

          if (wkts > bestW) {
            bestW = wkts;
            bestR = runsGv;
          } else if (wkts === bestW && runsGv < bestR) {
            bestR = runsGv;
          }
        }

        // Fielding contributions check
        (inn.batsmen || []).forEach(bt => {
          if (bt.outFielder === name) catches++;
          if (bt.outType === 'RunOut' && (bt.outFielder === name || (bt.runOutBy && bt.runOutBy.includes(name)))) runOuts++;
        });
      });
    });

    // Merge cloud performances from matches NOT saved on this phone.
    const localMatchIds = new Set($savedMatches.map(m => m.id));
    cloudPerformances.forEach(perf => {
      if (localMatchIds.has(perf.matchId)) return;
      matchesCount++;
      if (perf.won === true) wins++;
      else if (perf.won === false) losses++;

      if (perf.mom) momCount++;

      const bat = perf.batting || {};
      const bowl = perf.bowling || {};
      const batRuns = bat.runs || 0;

      r += batRuns;
      b += bat.balls || 0;
      sx += bat.sixes || 0;
      fr += bat.fours || 0;
      if (batRuns > hs) hs = batRuns;
      if (bat.outs) outs += bat.outs;
      if (batRuns >= 100) hundreds++;
      else if (batRuns >= 50) fifties++;
      else if (batRuns >= 30) thirties++;

      const bowlWkts = bowl.wickets || 0;
      const bowlRuns = bowl.runs || 0;
      w += bowlWkts;
      rc += bowlRuns;
      bb += bowl.balls || 0;
      md += bowl.maidens || 0;

      if (bowlWkts >= 5) fiveW++;
      else if (bowlWkts >= 3) threeW++;

      if (bowlWkts > bestW) {
        bestW = bowlWkts;
        bestR = bowlRuns;
      } else if (bowlWkts === bestW && bowlRuns < bestR && bestW > 0) {
        bestR = bowlRuns;
      }
    });

    const winRate = matchesCount > 0 ? Math.round((wins / matchesCount) * 100) : 0;
    const batAvgStr = outs > 0 ? (r / outs).toFixed(2) : String(r);
    const srStr = b > 0 ? ((r / b) * 100).toFixed(1) : '0.0';
    const boundaryPctStr = r > 0 ? (((fr * 4 + sx * 6) / r) * 100).toFixed(1) : '0.0';
    const bowlAvgStr = w > 0 ? (rc / w).toFixed(2) : '0.0';
    const bowlSrStr = w > 0 ? (bb / w).toFixed(1) : '0.0';
    const econStr = bb > 0 ? ((rc / bb) * 6).toFixed(2) : '0.00';

    // Achievement Badges
    const badgeList = [];
    if (sx >= 25) badgeList.push({ id: 'six_king', title: '💥 Six King', desc: '25+ Career Sixes' });
    if (bb >= 60 && parseFloat(econStr) < 6.0 && parseFloat(econStr) > 0) badgeList.push({ id: 'econ_master', title: '🎯 Economy Master', desc: 'Econ < 6.00 (min 10 overs)' });
    if (momCount >= 2) badgeList.push({ id: 'mvp_collector', title: '🏆 MVP Collector', desc: `${momCount} Player of Match awards` });
    if (b >= 30 && parseFloat(srStr) >= 160) badgeList.push({ id: 'fire_striker', title: '⚡ Fire Striker', desc: 'Strike Rate > 160' });
    if (hundreds > 0 || fifties > 0) badgeList.push({ id: 'milestone', title: '💯 Milestone Maker', desc: `${hundreds > 0 ? hundreds + ' Century' : fifties + ' Half-Century'}` });
    if (w >= 10) badgeList.push({ id: 'wicket_machine', title: '🎳 Wicket Machine', desc: '10+ Career Wickets' });
    if (wins >= 5 && winRate >= 60) badgeList.push({ id: 'champion', title: '👑 Winner', desc: `${winRate}% Win Rate` });

    stats = {
      matches: matchesCount,
      wins,
      losses,
      winRate,
      momCount,

      runs: r,
      balls: b,
      outs,
      notOuts,
      fifties,
      hundreds,
      thirties,
      sixes: sx,
      fours: fr,
      highestScore: hs,
      battingAvg: batAvgStr,
      strikeRate: srStr,
      boundaryRunsPct: boundaryPctStr,

      wickets: w,
      runsConceded: rc,
      ballsBowled: bb,
      maidens: md,
      bestBowlingWickets: bestW,
      bestBowlingRuns: bestW > 0 ? bestR : 0,
      threeWickets: threeW,
      fiveWickets: fiveW,
      bowlingAvg: bowlAvgStr,
      bowlingSR: bowlSrStr,
      economy: econStr,

      catches,
      runOuts,
      badges: badgeList
    };
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = function(event) {
      const img = new Image();
      img.onload = function() {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const size = 180;
        canvas.width = size;
        canvas.height = size;

        const min = Math.min(img.width, img.height);
        const sx = (img.width - min) / 2;
        const sy = (img.height - min) / 2;
        ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);

        photoBase64 = canvas.toDataURL('image/jpeg', 0.8);
      };
      img.src = /** @type {string} */ (event.target.result);
    };
    reader.readAsDataURL(file);
  }

  async function handleSave() {
    const cleanUsername = username.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    const cleanName = displayName.trim();
    const cleanPhone = phoneInput.trim();

    if (!cleanName) {
      errorMsg = "Display Name cannot be empty.";
      return;
    }

    if (!usernameLocked) {
      if (!cleanUsername) {
        errorMsg = "Username cannot be empty (letters, numbers, underscore only).";
        return;
      }
      if (cleanUsername.length > 20) {
        errorMsg = "Username must be 20 characters or less.";
        return;
      }
      if (cleanUsername.length < 3) {
        errorMsg = "Username must be at least 3 characters.";
        return;
      }

      isCheckingUsername = true;
      isSaving = true;
      errorMsg = "";
      message = "";

      const check = await checkUsernameAvailability(cleanUsername, $currentUser.uid);
      if (!check.available) {
        isCheckingUsername = false;
        isSaving = false;
        errorMsg = check.offline
          ? "Can't verify the username right now — check your internet connection and try again."
          : `Username "${cleanUsername}" is already taken! Choose a different one.`;
        return;
      }

      // Claim atomically — usernames are permanent, so a race between two
      // devices must have exactly one winner.
      const claim = await claimUsername(cleanUsername, $currentUser.uid);
      isCheckingUsername = false;
      if (!claim.ok) {
        isSaving = false;
        errorMsg = claim.reason === 'taken'
          ? `Username "${cleanUsername}" was just taken by someone else! Choose a different one.`
          : "Network problem while reserving the username. Try again.";
        return;
      }
    }

    isSaving = true;
    errorMsg = "";
    message = "";

    const profileData = {
      uid: $currentUser.uid,
      displayName: cleanName,
      username: usernameLocked ? username : cleanUsername,
      phoneNumber: cleanPhone,
      email: $currentUser.email || '',
      photo: photoBase64,
      role: role || '',
      battingStyle: battingStyle || '',
      bowlingType: showBowlingType ? (bowlingType || '') : '',
      age: age ? Number(age) : '',
      stats: {
        matches: stats.matches,
        runs: stats.runs,
        balls: stats.balls,
        wickets: stats.wickets,
        runsConceded: stats.runsConceded,
        ballsBowled: stats.ballsBowled,
        sixes: stats.sixes,
        fours: stats.fours,
        highestScore: stats.highestScore
      },
      updatedAt: new Date().toISOString()
    };

    try {
      currentUser.update(u => ({ ...u, displayName: cleanName, phoneNumber: cleanPhone }));
      const reg = JSON.parse(localStorage.getItem('cs_player_registry') || '{}');
      reg[$currentUser.uid] = profileData;
      reg[cleanName] = profileData;
      localStorage.setItem('cs_player_registry', JSON.stringify(reg));
    } catch (_) {}

    const ok = await pushUserProfileToCloud(profileData);
    isSaving = false;

    if (!usernameLocked && cleanUsername) {
      usernameLocked = true;
      username = cleanUsername;
    }

    if (ok) {
      message = "Profile synced to cloud successfully! 🎉";
    } else {
      message = "Saved locally. (Network offline - will sync when online).";
    }

    setTimeout(() => {
      message = "";
    }, 4000);
  }

  function goHome() {
    currentScreen.set('home');
  }

  async function handleLogout() {
    if (!(await confirmDialog('Logout from this device? Your local matches stay on the phone.', { title: 'Logout' }))) return;
    auth.signOut();
    goHome();
  }

  // Google Play policy: apps that let people create accounts must also let
  // them delete the account (and its data) inside the app.
  let isDeleting = false;
  async function handleDeleteAccount() {
    const first = await confirmDialog(
      'Delete your account permanently?\n\nThis erases your cloud profile, username and cloud match backups. Matches saved on this phone are not touched.',
      { title: 'Delete account', okText: 'Continue', danger: true }
    );
    if (!first) return;
    const typed = await promptDialog('Type DELETE to confirm permanent account deletion:', { title: 'Final confirmation', placeholder: 'DELETE' });
    if (typed === null) return;
    if (String(typed).trim().toUpperCase() !== 'DELETE') {
      toast('Deletion cancelled — text did not match.', 'info');
      return;
    }
    isDeleting = true;
    try {
      await deleteAccountAndData();
      try { localStorage.removeItem('cs_player_registry'); } catch (_) {}
      toast('Your account and cloud data have been deleted.', 'success', 5000);
      goHome();
    } catch (error) {
      if (error?.code === 'auth/requires-recent-login') {
        await alertDialog('For security, please logout, login again, and then delete the account (Google requires a recent login for this).', { title: 'Re-login needed' });
      } else {
        toast('Could not delete the account: ' + (error?.message || 'unknown error'), 'error', 5000);
      }
    } finally {
      isDeleting = false;
    }
  }
</script>

<div class="screen" id="profile-screen">
  <button class="btn btn-ghost" on:click={goHome}>← Back</button>
  
  <h1 style="color:#fbbf24; margin-top:10px; display:flex; align-items:center; gap:8px;">👤 Player Profile</h1>

  <div class="card">
    <div style="display:flex; flex-direction:column; align-items:center; justify-content:center; margin-bottom:20px; position:relative;">
      {#if photoBase64}
        <img src={photoBase64} alt="Avatar" style="width:110px; height:110px; border-radius:50%; border:3px solid #fbbf24; object-fit:cover; box-shadow:0 10px 25px rgba(251,191,36,0.25);" />
      {:else}
        <div style="width:110px; height:110px; border-radius:50%; background:#1e293b; border:3px dashed #475569; display:flex; align-items:center; justify-content:center; font-size:42px; color:#64748b;">
          🏏
        </div>
      {/if}

      <label for="profile-photo-upload" class="upload-badge">
        📸 Edit
        <input type="file" id="profile-photo-upload" accept="image/*" on:change={handleFileChange} style="display:none;" />
      </label>
    </div>

    <!-- Email Field (Readonly) -->
    {#if $currentUser?.email}
    <div class="field">
      <label for="profile-email" style="color:#94a3b8; font-weight:800; font-size:11px;">EMAIL ADDRESS (Google Sync)</label>
      <input type="text" id="profile-email" value={$currentUser.email} readonly style="width:100%; padding:12px; background:#0f172a; border:1px solid #1e293b; color:#64748b; border-radius:6px; font-weight:800; opacity:0.7;" />
    </div>
    {/if}

    <div class="field" style="margin-top:15px;">
      <label for="profile-phone" style="color:#94a3b8; font-weight:800; font-size:11px;">PHONE NUMBER (Optional)</label>
      <input type="tel" id="profile-phone" bind:value={phoneInput} placeholder="e.g. 9876543210" style="width:100%; padding:12px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; font-weight:800;" />
    </div>

    <div class="field" style="margin-top:15px;">
      <label for="profile-username" style="color:#94a3b8; font-weight:800; font-size:11px;">
        USERNAME (For others to search)
        {#if usernameLocked}
          <span style="color:#fbbf24; margin-left:6px;">🔒 LOCKED</span>
        {:else}
          <span style="color:#64748b; margin-left:6px;">({username.trim().length}/20)</span>
        {/if}
      </label>
      {#if usernameLocked}
        <input type="text" id="profile-username" value={username} readonly
          style="width:100%; padding:12px; background:#0f172a; border:1px solid #fbbf24; color:#fbbf24; border-radius:6px; font-weight:800; cursor:not-allowed; opacity:0.8;" />
        <small style="color:#64748b; font-size:10px; font-weight:600; margin-top:4px; display:block;">⚠️ Username is permanent and cannot be changed once saved.</small>
      {:else}
        <input type="text" id="profile-username" bind:value={username} placeholder="e.g. virat_king_18" maxlength="20"
          style="width:100%; padding:12px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; font-weight:800;" />
        <small style="color:#64748b; font-size:10px; font-weight:600; margin-top:4px; display:block;">⚠️ Choose carefully! Username is permanent — you can't change it later.</small>
      {/if}
    </div>

    <div class="field" style="margin-top:15px;">
      <label for="profile-display-name" style="color:#94a3b8; font-weight:800; font-size:11px;">DISPLAY/ROSTER NAME (Match name)</label>
      <input type="text" id="profile-display-name" bind:value={displayName} placeholder="e.g. Virat Kohli" style="width:100%; padding:12px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; font-weight:800;" />
    </div>

    <!-- Player identity: role / style / bowling type / age -->
    <div style="margin-top:15px; padding:12px; background:rgba(15,23,42,0.5); border:1px solid #334155; border-radius:10px;">
      <div style="color:#fbbf24; font-weight:900; font-size:11px; letter-spacing:0.5px; margin-bottom:10px;">🏏 PLAYER PROFILE</div>

      <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
        <div class="field" style="margin:0;">
          <label for="p-role" style="color:#94a3b8; font-weight:800; font-size:10px;">ROLE</label>
          <select id="p-role" bind:value={role} style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; font-weight:700; font-size:13px;">
            <option value="">Select…</option>
            {#each ROLES as r, i_each (r.id || r.name || i_each)}<option value={r}>{r}</option>{/each}
          </select>
        </div>
        <div class="field" style="margin:0;">
          <label for="p-age" style="color:#94a3b8; font-weight:800; font-size:10px;">AGE</label>
          <input type="number" id="p-age" bind:value={age} min="5" max="99" placeholder="e.g. 24" style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; font-weight:700; font-size:13px;" />
        </div>
        <div class="field" style="margin:0;">
          <label for="p-bat" style="color:#94a3b8; font-weight:800; font-size:10px;">BATTING</label>
          <select id="p-bat" bind:value={battingStyle} style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; font-weight:700; font-size:13px;">
            <option value="">Select…</option>
            {#each BATTING_STYLES as b, i_each (b.id || b.name || i_each)}<option value={b}>{b}</option>{/each}
          </select>
        </div>
        {#if showBowlingType}
          <div class="field" style="margin:0;">
            <label for="p-bowl" style="color:#94a3b8; font-weight:800; font-size:10px;">BOWLING TYPE</label>
            <select id="p-bowl" bind:value={bowlingType} style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; font-weight:700; font-size:13px;">
              <option value="">Select…</option>
              {#each BOWLING_TYPES as t, i_each (t.id || t.name || i_each)}<option value={t}>{t}</option>{/each}
            </select>
          </div>
        {/if}
      </div>
    </div>

    {#if errorMsg}
      <div style="color:#ef4444; font-size:13px; font-weight:800; margin-top:12px; text-align:center;">⚠️ {errorMsg}</div>
    {/if}

    {#if message}
      <div style="color:#10b981; font-size:13px; font-weight:800; margin-top:12px; text-align:center;">✨ {message}</div>
    {/if}

    <div style="display:flex; justify-content:space-between; align-items:center; margin-top:20px; gap:10px;">
      <button class="btn btn-primary" style="flex:1; font-size:15px; font-weight:800; margin:0;" on:click={handleSave} disabled={isSaving}>
        {#if isCheckingUsername}
          CHECKING USERNAME...
        {:else if isSaving}
          SYNCING...
        {:else}
          SAVE & SYNC 💾
        {/if}
      </button>
      <button class="btn btn-ghost" style="border:1px solid #ef4444; color:#ef4444; padding:12px; font-weight:800; margin:0;" on:click={handleLogout}>
        🚪 LOGOUT
      </button>
    </div>

    <button class="btn btn-ghost" style="width:100%; margin-top:14px; border:1px dashed #7f1d1d; color:#f87171; font-size:12px; font-weight:700;" disabled={isDeleting} on:click={handleDeleteAccount}>
      {isDeleting ? 'DELETING ACCOUNT…' : 'Delete my account & cloud data permanently'}
    </button>
  </div>

  <!-- CAREER STATS PANEL -->
  <div class="card" style="margin-top:18px;">
    <div style="font-weight:800; margin-bottom:14px; letter-spacing:1px; color:#fbbf24; font-size:14px; text-transform:uppercase; display:flex; align-items:center; justify-content:space-between; gap:6px;">
      <span>📈 CAREER STATS</span>
      <button class="btn btn-ghost" style="padding:6px 12px; font-size:11px; border:1px solid #34d399; color:#6ee7b7; text-transform:none; letter-spacing:0;" disabled={isSharingCard} on:click={shareStatsCard}>
        {isSharingCard ? 'Rendering…' : '🖼️ Share Card'}
      </button>
    </div>

    <!-- MATCH IMPACT OVERVIEW GRID -->
    <div style="display:grid; grid-template-columns:repeat(4, 1fr); gap:8px;">
      <div style="background:#0f172a; padding:10px 4px; border-radius:8px; border:1px solid #1e293b; text-align:center;">
        <div style="font-size:10px; color:#94a3b8; font-weight:800;">MATCHES</div>
        <div style="font-size:20px; font-weight:900; color:#fff; margin-top:2px;">{stats.matches}</div>
      </div>
      <div style="background:#0f172a; padding:10px 4px; border-radius:8px; border:1px solid #1e293b; text-align:center;">
        <div style="font-size:10px; color:#94a3b8; font-weight:800;">WIN RATE</div>
        <div style="font-size:20px; font-weight:900; color:#34d399; margin-top:2px;">{stats.matches > 0 ? stats.winRate + '%' : '—'}</div>
      </div>
      <div style="background:#0f172a; padding:10px 4px; border-radius:8px; border:1px solid #1e293b; text-align:center;">
        <div style="font-size:10px; color:#94a3b8; font-weight:800;">RUNS</div>
        <div style="font-size:20px; font-weight:900; color:#fbbf24; margin-top:2px;">{stats.runs}</div>
      </div>
      <div style="background:#0f172a; padding:10px 4px; border-radius:8px; border:1px solid #1e293b; text-align:center;">
        <div style="font-size:10px; color:#94a3b8; font-weight:800;">WICKETS</div>
        <div style="font-size:20px; font-weight:900; color:#60a5fa; margin-top:2px;">{stats.wickets}</div>
      </div>
    </div>

    <!-- MVP POINTS & RANK TIER BANNER -->
    <div style="display:flex; justify-content:space-between; align-items:center; background:linear-gradient(135deg, rgba(251,191,36,0.15), rgba(245,158,11,0.25)); border:1px solid rgba(251,191,36,0.4); padding:10px 14px; border-radius:8px; margin-top:12px;">
      <div>
        <div style="font-size:10px; font-weight:800; color:#fbbf24; text-transform:uppercase;">CAREER MVP POINTS</div>
        <div style="font-size:22px; font-weight:900; color:#fff;">{mvpPoints} <span style="font-size:12px; color:#cbd5e1;">PTS</span></div>
      </div>
      <div style="background:{mvpTier.color}; color:#0f172a; padding:6px 12px; border-radius:20px; font-weight:900; font-size:12px; box-shadow:0 0 12px {mvpTier.color}40;">
        {mvpTier.title}
      </div>
    </div>

    <!-- WINS & MOM SUMMARY ROW -->
    <div style="display:flex; justify-content:space-between; align-items:center; background:rgba(30,41,59,0.5); padding:8px 12px; border-radius:6px; margin-top:10px; font-size:12px; font-weight:800;">
      <span style="color:#cbd5e1;">🏆 Player of Match: <strong style="color:#fbbf24;">{stats.momCount}</strong></span>
      <span style="color:#cbd5e1;">🎯 Record: <strong style="color:#34d399;">{stats.wins}W</strong> - <strong style="color:#f87171;">{stats.losses}L</strong></span>
    </div>

    <!-- BATTING SECTION -->
    <div style="margin-top:16px; border-top:1px dashed #334155; padding-top:14px;">
      <div style="font-size:12px; font-weight:800; color:#fbbf24; margin-bottom:8px; text-transform:uppercase; display:flex; justify-content:space-between; align-items:center;">
        <span>🏏 BATTING METRICS</span>
        <span style="font-size:10px; color:#94a3b8; font-weight:700;">Balls Faced: {stats.balls}</span>
      </div>

      <div style="display:grid; grid-template-columns:repeat(3, 1fr); gap:8px;">
        <div style="background:#0f172a; padding:8px; border-radius:6px; text-align:center; font-size:12px;">
          <span style="color:#64748b; font-weight:800; font-size:10px; display:block;">AVG</span>
          <span style="font-weight:900; color:#fff;">{stats.battingAvg}</span>
        </div>
        <div style="background:#0f172a; padding:8px; border-radius:6px; text-align:center; font-size:12px;">
          <span style="color:#64748b; font-weight:800; font-size:10px; display:block;">S/R</span>
          <span style="font-weight:900; color:#fff;">{stats.strikeRate}</span>
        </div>
        <div style="background:#0f172a; padding:8px; border-radius:6px; text-align:center; font-size:12px;">
          <span style="color:#64748b; font-weight:800; font-size:10px; display:block;">H.S.</span>
          <span style="font-weight:900; color:#fff;">{stats.highestScore}</span>
        </div>
      </div>

      <div style="display:grid; grid-template-columns:repeat(3, 1fr); gap:8px; margin-top:8px;">
        <div style="background:#0f172a; padding:8px; border-radius:6px; text-align:center; font-size:12px;">
          <span style="color:#64748b; font-weight:800; font-size:10px; display:block;">NOT OUTS</span>
          <span style="font-weight:900; color:#fff;">{stats.notOuts}</span>
        </div>
        <div style="background:#0f172a; padding:8px; border-radius:6px; text-align:center; font-size:12px;">
          <span style="color:#64748b; font-weight:800; font-size:10px; display:block;">50s / 100s</span>
          <span style="font-weight:900; color:#fff;">{stats.fifties} / {stats.hundreds}</span>
        </div>
        <div style="background:#0f172a; padding:8px; border-radius:6px; text-align:center; font-size:12px;">
          <span style="color:#64748b; font-weight:800; font-size:10px; display:block;">BOUNDARY %</span>
          <span style="font-weight:900; color:#fff;">{stats.boundaryRunsPct}%</span>
        </div>
      </div>

      <div style="display:flex; justify-content:space-around; margin-top:10px; font-size:13px; font-weight:800;">
        <span style="color:#a1a1aa;">💥 Sixes: <strong style="color:#fbbf24;">{stats.sixes}</strong></span>
        <span style="color:#a1a1aa;">☄️ Fours: <strong style="color:#60a5fa;">{stats.fours}</strong></span>
        <span style="color:#a1a1aa;">🔥 30s: <strong style="color:#34d399;">{stats.thirties}</strong></span>
      </div>
    </div>

    <!-- BOWLING SECTION -->
    <div style="margin-top:16px; border-top:1px dashed #334155; padding-top:14px;">
      <div style="font-size:12px; font-weight:800; color:#60a5fa; margin-bottom:8px; text-transform:uppercase; display:flex; justify-content:space-between; align-items:center;">
        <span>🎳 BOWLING METRICS</span>
        <span style="font-size:10px; color:#94a3b8; font-weight:700;">Overs: {formatOvers(stats.ballsBowled)}</span>
      </div>

      <div style="display:grid; grid-template-columns:repeat(3, 1fr); gap:8px;">
        <div style="background:#0f172a; padding:8px; border-radius:6px; text-align:center; font-size:12px;">
          <span style="color:#64748b; font-weight:800; font-size:10px; display:block;">WICKETS</span>
          <span style="font-weight:900; color:#fff;">{stats.wickets}</span>
        </div>
        <div style="background:#0f172a; padding:8px; border-radius:6px; text-align:center; font-size:12px;">
          <span style="color:#64748b; font-weight:800; font-size:10px; display:block;">ECON</span>
          <span style="font-weight:900; color:#fff;">{stats.economy}</span>
        </div>
        <div style="background:#0f172a; padding:8px; border-radius:6px; text-align:center; font-size:12px;">
          <span style="color:#64748b; font-weight:800; font-size:10px; display:block;">B.B.</span>
          <span style="font-weight:900; color:#fff;">{stats.bestBowlingWickets > 0 ? `${stats.bestBowlingWickets}/${stats.bestBowlingRuns}` : '—'}</span>
        </div>
      </div>

      <div style="display:grid; grid-template-columns:repeat(3, 1fr); gap:8px; margin-top:8px;">
        <div style="background:#0f172a; padding:8px; border-radius:6px; text-align:center; font-size:12px;">
          <span style="color:#64748b; font-weight:800; font-size:10px; display:block;">BOWLING AVG</span>
          <span style="font-weight:900; color:#fff;">{stats.bowlingAvg}</span>
        </div>
        <div style="background:#0f172a; padding:8px; border-radius:6px; text-align:center; font-size:12px;">
          <span style="color:#64748b; font-weight:800; font-size:10px; display:block;">BOWLING S/R</span>
          <span style="font-weight:900; color:#fff;">{stats.bowlingSR}</span>
        </div>
        <div style="background:#0f172a; padding:8px; border-radius:6px; text-align:center; font-size:12px;">
          <span style="color:#64748b; font-weight:800; font-size:10px; display:block;">3W / 5W</span>
          <span style="font-weight:900; color:#fff;">{stats.threeWickets} / {stats.fiveWickets}</span>
        </div>
      </div>

      <div style="display:flex; justify-content:space-around; margin-top:10px; font-size:13px; font-weight:800;">
        <span style="color:#a1a1aa;">🎯 Maidens: <strong style="color:#34d399;">{stats.maidens}</strong></span>
        <span style="color:#a1a1aa;">🏃 Runs Conceded: <strong style="color:#f87171;">{stats.runsConceded}</strong></span>
      </div>
    </div>

    <!-- FIELDING SECTION -->
    <div style="margin-top:16px; border-top:1px dashed #334155; padding-top:14px;">
      <div style="font-size:12px; font-weight:800; color:#34d399; margin-bottom:8px; text-transform:uppercase;">🧤 FIELDING & ALL-ROUND IMPACT</div>
      <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px;">
        <div style="background:#0f172a; padding:10px; border-radius:6px; text-align:center;">
          <span style="color:#64748b; font-weight:800; font-size:11px; display:block;">CATCHES TAKEN</span>
          <span style="font-size:18px; font-weight:900; color:#fff;">{stats.catches}</span>
        </div>
        <div style="background:#0f172a; padding:10px; border-radius:6px; text-align:center;">
          <span style="color:#64748b; font-weight:800; font-size:11px; display:block;">RUN OUTS</span>
          <span style="font-size:18px; font-weight:900; color:#fff;">{stats.runOuts}</span>
        </div>
      </div>
    </div>

    <!-- CAREER MILESTONES PROGRESS TRACKER -->
    <div style="margin-top:16px; border-top:1px dashed #334155; padding-top:14px;">
      <div style="font-size:12px; font-weight:800; color:#38bdf8; margin-bottom:10px; text-transform:uppercase;">🎯 CAREER MILESTONE TRACKER</div>
      
      <!-- 500 Runs Milestone -->
      <div style="margin-bottom:10px;">
        <div style="display:flex; justify-content:space-between; font-size:11px; font-weight:800; color:#cbd5e1; margin-bottom:4px;">
          <span>🏏 500 Career Runs</span>
          <span style="color:#fbbf24;">{stats.runs} / 500</span>
        </div>
        <div style="width:100%; height:8px; background:#0f172a; border-radius:4px; overflow:hidden; border:1px solid #1e293b;">
          <div style="width:{Math.min(100, Math.round((stats.runs / 500) * 100))}%; height:100%; background:linear-gradient(90deg, #f59e0b, #fbbf24); border-radius:4px; transition:width 0.5s ease;"></div>
        </div>
      </div>

      <!-- 50 Wickets Milestone -->
      <div>
        <div style="display:flex; justify-content:space-between; font-size:11px; font-weight:800; color:#cbd5e1; margin-bottom:4px;">
          <span>🎳 50 Wickets</span>
          <span style="color:#60a5fa;">{stats.wickets} / 50</span>
        </div>
        <div style="width:100%; height:8px; background:#0f172a; border-radius:4px; overflow:hidden; border:1px solid #1e293b;">
          <div style="width:{Math.min(100, Math.round((stats.wickets / 50) * 100))}%; height:100%; background:linear-gradient(90deg, #2563eb, #60a5fa); border-radius:4px; transition:width 0.5s ease;"></div>
        </div>
      </div>
    </div>

    <!-- ACHIEVEMENTS & BADGES -->
    {#if stats.badges && stats.badges.length > 0}
    <div style="margin-top:16px; border-top:1px dashed #334155; padding-top:14px;">
      <div style="font-size:12px; font-weight:800; color:#a78bfa; margin-bottom:10px; text-transform:uppercase;">🏅 ACHIEVEMENTS & BADGES</div>
      <div style="display:flex; flex-wrap:wrap; gap:8px;">
        {#each stats.badges as badge, i_each (badge.id || badge.name || i_each)}
          <div style="background:linear-gradient(135deg, rgba(167,139,250,0.15), rgba(99,102,241,0.25)); border:1px solid rgba(167,139,250,0.4); padding:6px 12px; border-radius:20px; display:flex; align-items:center; gap:6px; box-shadow:0 0 10px rgba(167,139,250,0.2);">
            <span style="font-weight:900; font-size:12px; color:#fff;">{badge.title}</span>
            <span style="font-size:10px; color:#c4b5fd; font-weight:600;">({badge.desc})</span>
          </div>
        {/each}
      </div>
    </div>
    {/if}
  </div>
</div>

<style>
  .upload-badge {
    position: absolute;
    bottom: -6px;
    background: #fbbf24;
    color: #0f172a;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 800;
    cursor: pointer;
    box-shadow: 0 4px 10px rgba(0,0,0,0.3);
    transition: all 0.2s;
  }
  .upload-badge:hover {
    transform: scale(1.08);
  }
</style>
